import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { Class, ClassCreateRequest } from '../types';
import { GRADE_OPTIONS, MISSING_TEACHER_LABEL, SCHOOL_YEARS, getCurrentSchoolYear, normalizeSchoolYear } from '../constants';
import { useSchools } from '../hooks/useSchools';
import { useSchoolTeacherOptions } from '../hooks/useSchoolTeacherOptions';

interface CreateClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ClassCreateRequest) => Promise<void>;
  isSubmitting: boolean;
  /** Có giá trị → chế độ sửa, form điền sẵn thông tin lớp này. */
  initialData?: Class | null;
}

const SUBJECTS = [
  { value: 'math', label: 'Toán học' },
  { value: 'physics', label: 'Vật lý' },
  { value: 'chemistry', label: 'Hóa học' },
  { value: 'biology', label: 'Sinh học' },
  { value: 'literature', label: 'Ngữ văn' },
  { value: 'history', label: 'Lịch sử' },
  { value: 'geography', label: 'Địa lý' },
  { value: 'english', label: 'Tiếng Anh' },
  { value: 'informatics', label: 'Tin học' },
  { value: 'physical', label: 'Thể dục' },
];

type Option = { value: string; label: string };

const toOptions = (values: readonly string[]): Option[] =>
  values.map((value) => ({ value, label: value }));

// Giá trị đang lưu có thể không nằm trong danh sách chọn sẵn → thêm vào để select không hiện trống.
const withCurrentOption = (options: Option[], current: string, currentLabel = current): Option[] =>
  current && !options.some((option) => option.value === current)
    ? [...options, { value: current, label: currentLabel }]
    : options;

// BE có thể trả null hoặc số (grade, schoolYear) → quy về chuỗi để select khớp và validate .trim() không lỗi.
const toText = (value: unknown) => (value == null ? '' : String(value));

type OptionalWhenEditing = 'code' | 'subject' | 'grade' | 'schoolYear';

const toFormData = (initialData?: Class | null): ClassCreateRequest => ({
  code: toText(initialData?.code),
  teacherId: toText(initialData?.teacherId),
  name: toText(initialData?.name),
  schoolId: toText(initialData?.schoolId),
  subject: toText(initialData?.subject),
  grade: toText(initialData?.grade),
  schoolYear: initialData ? normalizeSchoolYear(initialData.schoolYear) : getCurrentSchoolYear(),
  studentCount: initialData?.studentCount ?? 0,
  teacherCount: initialData?.teacherCount ?? 0,
  status: initialData?.status ?? 'active',
  invites: initialData?.invites ?? [],
  metadata: initialData?.metadata ?? {},
});

const CreateClassModal: React.FC<CreateClassModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  initialData,
}) => {
  const isEdit = !!initialData;
  const [formData, setFormData] = useState<ClassCreateRequest>(() => toFormData(initialData));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: schools = [], isLoading: isLoadingSchools } = useSchools();
  const { data: teachers = [], isLoading: isLoadingTeachers, isError: isTeacherLoadError, } = useSchoolTeacherOptions(
    formData.schoolId || undefined
  );

  // Chỉ điền lại khi mở popup hoặc đổi lớp; dữ liệu lớp refetch ngầm không được xoá những gì đang nhập.
  useEffect(() => {
    if (isOpen) {
      setFormData(toFormData(initialData));
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData?.id]);

  const subjectOptions = withCurrentOption(SUBJECTS, formData.subject);
  const gradeOptions = withCurrentOption(GRADE_OPTIONS, formData.grade);
  const schoolYearOptions = withCurrentOption(toOptions(SCHOOL_YEARS), formData.schoolYear);
  // Luôn giữ giáo viên hiện tại trong danh sách để ô chọn không bị trống; chỉ khi đã tải xong
  // mà không thấy mới biết giáo viên đó không còn trong trường.
  const isTeacherListReady = !isLoadingTeachers && !isTeacherLoadError;
  const teacherOptions = withCurrentOption(
    teachers,
    formData.teacherId,
    initialData?.teacher?.name ?? (isTeacherListReady ? MISSING_TEACHER_LABEL : 'Giáo viên hiện tại')
  );
  // Chỉ chế độ tạo mới cần chờ danh sách để biết có bắt chọn GVCN không; khi sửa thì không bắt chọn.
  const isWaitingForTeachers = !isEdit && isLoadingTeachers;
  // Khi sửa không bắt chọn để lớp cũ chưa có GVCN vẫn lưu được; trường chưa có giáo viên thì không có gì để chọn.
  const isTeacherRequired = !isEdit && teachers.length > 0;
  // Lớp tạo qua import (lớp, học sinh) thường thiếu mã lớp, môn học… nhưng vẫn phải sửa được để gán chủ nhiệm.
  // Khi sửa chỉ bắt các trường lớp đã có giá trị, để không xoá trống dữ liệu đang có.
  const isRequired = (field: OptionalWhenEditing) => !isEdit || toText(initialData?.[field]).trim() !== '';
  const requiredMark = (field: OptionalWhenEditing) => (isRequired(field) ? ' *' : '');
  const schoolOptions = withCurrentOption(
    schools.map((school) => ({ value: String(school.id), label: `${school.name} (${school.code})` })),
    formData.schoolId,
    initialData?.school?.name ?? 'Trường hiện tại'
  );

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!isEdit && isTeacherLoadError) {
      newErrors.teacherId = 'Không thể tải danh sách giáo viên. Vui lòng thử lại.';
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Tên lớp học là bắt buộc';
    }

    if (isRequired('code') && !formData.code.trim()) {
      newErrors.code = 'Mã lớp học là bắt buộc';
    }

    if (isRequired('subject') && !formData.subject.trim()) {
      newErrors.subject = 'Môn học là bắt buộc';
    }

    if (isRequired('grade') && !formData.grade.trim()) {
      newErrors.grade = 'Khối là bắt buộc';
    }

    if (isRequired('schoolYear') && !formData.schoolYear.trim()) {
      newErrors.schoolYear = 'Năm học là bắt buộc';
    }

    if (!formData.schoolId.trim()) {
      newErrors.schoolId = 'Trường học là bắt buộc';
    }

    if (isTeacherRequired && !formData.teacherId.trim()) {
      newErrors.teacherId = 'Giáo viên chủ nhiệm là bắt buộc';
    }

    if (formData.studentCount !== undefined && formData.studentCount < 0) {
      newErrors.studentCount = 'Sĩ số không được âm';
    }

    if (formData.teacherCount !== undefined && formData.teacherCount < 0) {
      newErrors.teacherCount = 'Số giáo viên không được âm';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      handleReset();
      onClose();
    } catch (error) {
      // Error handling được xử lý ở component cha
    }
  };

  const handleReset = () => {
    setFormData(toFormData());
    setErrors({});
  };

  const handleInputChange = (field: keyof ClassCreateRequest, value: string | number) => {
    // Giáo viên thuộc trường → đổi trường thì bỏ giáo viên đã chọn.
    setFormData(prev => ({ ...prev, [field]: value, ...(field === 'schoolId' ? { teacherId: '' } : {}) }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      handleReset();
      onClose();
    }
  };



  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto p-0">
        <div className="flex flex-col">
          {/* Header với background */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <DialogHeader className="space-y-1">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                {isEdit ? 'Sửa thông tin lớp học' : 'Tạo lớp học mới'}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-600">
                {isEdit
                  ? 'Cập nhật thông tin của lớp học'
                  : 'Nhập thông tin chi tiết để tạo lớp học mới trong hệ thống'}
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Form content */}
          <div className="px-6 py-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Tên lớp học */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Tên lớp học *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Nhập tên lớp học"
                    disabled={isSubmitting}
                    className={`h-10 ${errors.name ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Mã lớp học */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Mã lớp học{requiredMark('code')}
                  </label>
                  <Input
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                    placeholder="Nhập mã lớp học"
                    disabled={isSubmitting}
                    className={`h-10 ${errors.code ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                  />
                  {errors.code && (
                    <p className="text-xs text-red-500 mt-1">{errors.code}</p>
                  )}
                </div>

                {/* Môn học */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Môn học{requiredMark('subject')}
                  </label>
                  <select
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    disabled={isSubmitting}
                    className={`h-10 w-full rounded-md border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                      errors.subject ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Chọn môn học</option>
                    {subjectOptions.map((subject) => (
                      <option key={subject.value} value={subject.value}>
                        {subject.label}
                      </option>
                    ))}
                  </select>
                  {errors.subject && (
                    <p className="text-xs text-red-500 mt-1">{errors.subject}</p>
                  )}
                </div>

                {/* Khối */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Khối{requiredMark('grade')}
                  </label>
                  <select
                    value={formData.grade}
                    onChange={(e) => handleInputChange('grade', e.target.value)}
                    disabled={isSubmitting}
                    className={`h-10 w-full rounded-md border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                      errors.grade ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Chọn khối</option>
                    {gradeOptions.map((grade) => (
                      <option key={grade.value} value={grade.value}>{grade.label}</option>
                    ))}
                  </select>
                  {errors.grade && (
                    <p className="text-xs text-red-500 mt-1">{errors.grade}</p>
                  )}
                </div>

                {/* Năm học */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Năm học{requiredMark('schoolYear')}
                  </label>
                  <select
                    value={formData.schoolYear}
                    onChange={(e) => handleInputChange('schoolYear', e.target.value)}
                    disabled={isSubmitting}
                    className={`h-10 w-full rounded-md border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                      errors.schoolYear ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Chọn năm học</option>
                    {schoolYearOptions.map((year) => (
                      <option key={year.value} value={year.value}>{year.label}</option>
                    ))}
                  </select>
                  {errors.schoolYear && (
                    <p className="text-xs text-red-500 mt-1">{errors.schoolYear}</p>
                  )}
                </div>

                {/* Trường học */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Trường học *
                  </label>
                  <select
                    value={formData.schoolId}
                    onChange={(e) => handleInputChange('schoolId', e.target.value)}
                    disabled={isSubmitting || isLoadingSchools}
                    className={`h-10 w-full rounded-md border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                      errors.schoolId ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">
                      {isLoadingSchools ? 'Đang tải...' : 'Chọn trường học'}
                    </option>
                    {schoolOptions.map((school) => (
                      <option key={school.value} value={school.value}>
                        {school.label}
                      </option>
                    ))}
                  </select>
                  {errors.schoolId && (
                    <p className="text-xs text-red-500 mt-1">{errors.schoolId}</p>
                  )}
                </div>

                {/* Giáo viên chủ nhiệm */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Giáo viên chủ nhiệm{isTeacherRequired ? ' *' : ''}
                  </label>
                  <select
                    value={formData.teacherId}
                    onChange={(e) => handleInputChange('teacherId', e.target.value)}
                    disabled={isSubmitting || !formData.schoolId || isLoadingTeachers}
                    className={`h-10 w-full rounded-md border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                      errors.teacherId ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">
                      {!formData.schoolId
                        ? 'Chọn trường học trước'
                        : isLoadingTeachers
                          ? 'Đang tải...'
                          : isTeacherLoadError
                            ? 'Không tải được danh sách giáo viên'
                            : teacherOptions.length === 0
                            ? 'Trường chưa có giáo viên'
                            : 'Chọn giáo viên'}
                    </option>
                    {teacherOptions.map((teacher) => (
                      <option key={teacher.value} value={teacher.value}>{teacher.label}</option>
                    ))}
                  </select>
                  {errors.teacherId && (
                    <p className="text-xs text-red-500 mt-1">{errors.teacherId}</p>
                  )}
                </div>

                {/* Sĩ số */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Sĩ số
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.studentCount}
                    onChange={(e) => handleInputChange('studentCount', parseInt(e.target.value) || 0)}
                    disabled={isSubmitting}
                    className={`h-10 ${errors.studentCount ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                  />
                  {errors.studentCount && (
                    <p className="text-xs text-red-500 mt-1">{errors.studentCount}</p>
                  )}
                </div>

                {/* Số giáo viên */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Số giáo viên
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.teacherCount}
                    onChange={(e) => handleInputChange('teacherCount', parseInt(e.target.value) || 0)}
                    disabled={isSubmitting}
                    className={`h-10 ${errors.teacherCount ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
                  />
                  {errors.teacherCount && (
                    <p className="text-xs text-red-500 mt-1">{errors.teacherCount}</p>
                  )}
                </div>

                {/* Trạng thái */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Trạng thái
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value as 'active' | 'inactive' | 'archived')}
                    disabled={isSubmitting}
                    className="h-10 w-full rounded-md border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50 border-gray-300"
                  >
                    <option value="active">Hoạt động</option>
                    <option value="inactive">Không hoạt động</option>
                    <option value="archived">Đã lưu trữ</option>
                  </select>
                </div>
              </div>

              {/* Footer với actions */}
              <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="px-6 py-2"
                >
                  Hủy
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting || isWaitingForTeachers}
                  disabled={isSubmitting || isWaitingForTeachers}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700"
                >
                  {isEdit
                    ? (isSubmitting ? 'Đang lưu...' : 'Lưu thay đổi')
                    : (isSubmitting ? 'Đang tạo...' : 'Tạo lớp học')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateClassModal;
